import { CheckoutSpinner } from "@bevco/Components/CheckoutSpinner"; // VERNDALE CUSTOMIZATION - import ValidateCartApiParameter
import { ValidateCartApiParameter } from "@bevco/Services/CustomCartValidationService"; // VERNDALE CUSTOMIZATION - import customValidateCart handler
import customValidateCart from "@bevco/Store/Handlers/CheckoutShipping/ValidateCart"; // VERNDALE CUSTOMIZATION - import customValidateCart handler
import "@bevco/Store/Handlers/CheckoutShipping/UpdateCart";
import "@bevco/Store/Handlers/Context/HandleError";
import { resolveFormAddressForValidation } from "@bevco/Store/Handlers/CheckoutShipping/ResolveGuestCheckoutAddress";
import { scrollToFirstFormError } from "@insite/client-framework/Common/CustomerSelectors";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import { CustomState } from "@bevco/Store/Reducers";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import clearMessages from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/ClearMessages";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import setInitialValues from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetInitialValues";
import setIsPreloadingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetIsPreloadingData";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Button from "@insite/mobius/Button";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal from "@insite/mobius/Modal";
import Page from "@insite/mobius/Page";
import Typography from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { Component, createContext } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapDispatchToProps = {
    preloadCheckoutShippingData: makeHandlerChainAwaitable(preloadCheckoutShippingData),
    setIsPreloadingData,
    // IEIXOP-6930: callback-based updateCart so carrier 400s do not continue checkout.
    updateCart,
    setInitialValues,
    clearMessages,
    customValidateCart: makeHandlerChainAwaitable(customValidateCart), // VERNDALE CUSTOMIZATION - add customValidateCart handler
    loadCurrentCart,
    // IEIXOP-6930
    closeCarrierAddressErrorModal: () => (dispatch: (action: unknown) => void) => {
        dispatch({
            type: "Custom/CheckoutShipping/SetCarrierAddressErrorModal",
            isCarrierAddressErrorModalOpen: false,
        });
    },
};

const mapStateToProps = (state: CustomState) => {
    const cartId = state.pages.checkoutShipping.cartId;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    return {
        cartId: state.pages.checkoutShipping.cartId,
        isPreloadingData: state.pages.checkoutShipping.isPreloadingData,
        reviewAndSubmitPageLink: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage"),
        location: getLocation(state),
        isUpdatingCustomer: state.pages.checkoutShipping.isUpdatingCustomer,
        cartState,
        shipToState: getShipToState(state, cartState.value?.shipToId),
        useBillingAddress: state.pages.checkoutShipping.useBillingAddress,
        shippingAddressFormState: state.pages.checkoutShipping.shippingAddressFormState,
        billingAddressFormState: state.pages.checkoutShipping.billingAddressFormState,
        cartPageLink: getPageLinkByPageType(state, "CartPage"),
        session: state.context.session,
        isCarrierAddressErrorModalOpen: !!state.checkoutShipping?.isCarrierAddressErrorModalOpen,
    };
};

type Props = PageProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasHistory;

type Validator = () => Promise<boolean>;

type State = {
    formSubmitAttempted: boolean;
    validators: {
        [key: string]: Validator | undefined;
    };
    isRedirecting: boolean;
    isValidatingCart: boolean;
};

export const CheckoutShippingFormContext = createContext<State>({
    formSubmitAttempted: false,
    validators: {},
    isRedirecting: false,
    isValidatingCart: false,
});

class CheckoutShippingPage extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            formSubmitAttempted: false,
            validators: {},
            isRedirecting: false,
            isValidatingCart: false,
        };
    }

    async componentDidMount() {
        if (!this.props.isPreloadingData) {
            const { cartId } = parseQueryString<{ cartId?: string }>(this.props.location.search);
            await this.props.preloadCheckoutShippingData({ cartId });
        }
        // Always hydrate checkout form state. When the cart page ran preload before navigation,
        // isPreloadingData is still true so the branch above is skipped - setInitialValues must still run.
        this.props.setInitialValues();
        this.props.setIsPreloadingData({ isPreloadingData: false });
        this.props.clearMessages();
    }
    // VERNDALE CUSTOMIZATION START
    // Here we build the cart to validate, and pass it to the customValidateCart handler
    buildCart = (): ValidateCartApiParameter => {
        const cart = this.props.cartState.value;
        const { useBillingAddress, shippingAddressFormState, billingAddressFormState, shipToState, session } =
            this.props;
        const isGuest = session.isAuthenticated && session.isGuest;
        const shipTo =
            resolveFormAddressForValidation(
                isGuest,
                useBillingAddress,
                billingAddressFormState?.address,
                shippingAddressFormState?.address,
                shipToState.value,
            ) ?? shipToState.value;

        if (!cart || !shipTo) {
            return {} as ValidateCartApiParameter;
        }

        const cartToValidate: ValidateCartApiParameter = {
            lines: [],
            orderNumber: cart.orderNumber || "",
            shipToCustNbr: shipTo.customerSequence || shipTo.customerNumber || "", // fallback for the selectedShipTo
            shipToRegion: shipTo.state?.abbreviation || "",
        };

        cart.cartLines?.forEach(line => {
            cartToValidate.lines.push({
                ProductNbr: line.erpNumber || "",
                CartQuantity: line.qtyOrdered?.toString() || "",
                QtyUom: line.unitOfMeasure || "",
                LooseEnds: line.properties?.looseEnds || "No",
                CartlineId: line.id || "",
                IsQuote: line.properties?.isQuote || false,
            });
        });

        return cartToValidate;
    };
    // VERNDALE CUSTOMIZATION END

    continueAfterCartUpdate = async () => {
        const cartToValidate = this.buildCart();
        await this.props.customValidateCart({
            apiParameter: cartToValidate,
            onComplete: resultProps => {
                this.setState({ isValidatingCart: false });
                if (resultProps.apiResult.results && resultProps.apiResult.results.length > 0) {
                    this.props.history.push(this.props.cartPageLink!.url);
                } else {
                    this.setState({ isRedirecting: true });
                    const continueUrl = this.props.cartId
                        ? `${this.props.reviewAndSubmitPageLink!.url}?cartId=${this.props.cartId}`
                        : this.props.reviewAndSubmitPageLink!.url;
                    this.props.history.push(continueUrl);
                }
            },
        });
    };

    handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!this.props.session.isAuthenticated) {
            window.location.reload();
            return;
        }

        this.setState({ formSubmitAttempted: true });

        const validators: Validator[] = [];
        Object.keys(this.state.validators).forEach(key => {
            const validator = this.state.validators[key];
            if (validator) {
                validators.push(validator);
            }
        });
        const results = await Promise.all(validators.map(v => v()));

        if (!results.every(o => o)) {
            scrollToFirstFormError();
            return;
        }

        // IEIXOP-6930: callback-based updateCart so carrier 400s do not continue checkout.
        // onSuccess -> existing validate/redirect; onException -> stay on page (modal via Redux).
        this.setState({ isValidatingCart: true });
        this.props.updateCart({
            onSuccess: () => {
                this.continueAfterCartUpdate().catch(() => {
                    this.setState({ isValidatingCart: false });
                });
            },
            onException: () => {
                // IEIXOP-6930: modal opened from UpdateCart; only clear the spinner here.
                this.setState({ isValidatingCart: false });
            },
        } as any);
    };

    // VERNDALE CUSTOMIZATION END

    // IEIXOP-6930
    handleCarrierErrorClose = () => {
        this.props.closeCarrierAddressErrorModal();
        this.setState({ isValidatingCart: false });
    };

    // IEIXOP-6930: proceed despite no carriers; cart already saved with empty carriers
    handleCarrierErrorContinueAnyway = () => {
        this.props.closeCarrierAddressErrorModal();
        this.setState({ isValidatingCart: true });
        // Force a full cart replace so Review & Pay does not keep prior carrier selection in memory.
        this.props.loadCurrentCart({ shouldLoadFullCart: true, replaceCart: true } as any);
        this.continueAfterCartUpdate().catch(() => {
            this.setState({ isValidatingCart: false });
        });
    };

    /** Renders the shipping form and the IEIXOP-6930 carrier address error modal. */
    render() {
        return (
            <Page data-test-selector="checkoutShipping">
                <LoadingOverlay loading={this.props.isUpdatingCustomer}>
                    <CheckoutShippingFormContext.Provider value={this.state}>
                        <form id="checkoutShippingForm" onSubmit={this.handleFormSubmit} noValidate>
                            <Zone zoneName="Content" contentId={this.props.id} />
                            {/* VERNDALE CUSTOMIZATION */}
                            {this.state.isValidatingCart && <CheckoutSpinner></CheckoutSpinner>}
                            {/* VERNDALE CUSTOMIZATION */}
                        </form>
                    </CheckoutShippingFormContext.Provider>
                </LoadingOverlay>
                {/* IEIXOP-6930: Shipping-only SiteMessage; Close edits address, Continue Anyway advances */}
                <Modal
                    headline={translate("Error")}
                    isOpen={this.props.isCarrierAddressErrorModalOpen}
                    handleClose={this.handleCarrierErrorClose}
                    size={550}
                    data-test-selector="checkoutShipping_carrierAddressErrorModal"
                >
                    <Typography as="p">{siteMessage("CheckoutShipping_InvalidAddressToShowCarrierServices")}</Typography>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 12,
                            marginTop: 20,
                        }}
                    >
                        <Button onClick={this.handleCarrierErrorClose} data-test-selector="checkoutShipping_carrierErrorClose">
                            {translate("Close")}
                        </Button>
                        <Button
                            onClick={this.handleCarrierErrorContinueAnyway}
                            data-test-selector="checkoutShipping_carrierErrorContinueAnyway"
                        >
                            {translate("Continue Anyway")}
                        </Button>
                    </div>
                </Modal>
            </Page>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutShippingPage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "CheckoutShippingPage" instead of this constant.
 */
export const CheckoutShippingPageContext = "CheckoutShippingPage";

